import { useState } from "react";
import { Download, FileText } from "lucide-react";
import { API_BASE, getStoredAuth } from "@/lib/apiClient";
import { V1 } from "@/constants/testIds";

const TYPES = [
  { value: "sales", label: "Sales" },
  { value: "purchase", label: "Purchase" },
  { value: "inventory", label: "Inventory" },
  { value: "qc", label: "Quality Check" },
  { value: "warranty", label: "Warranty" },
  { value: "case", label: "Customer Cases" },
  { value: "campaign", label: "Campaigns" },
];

export default function ReportsPage() {
  const [kind, setKind] = useState("sales");
  const [format, setFormat] = useState("xlsx");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const download = async () => {
    setBusy(true);
    setErr(null);
    try {
      const token = getStoredAuth()?.access_token;
      const params = new URLSearchParams({ format });
      if (start) params.set("start", start);
      if (end) params.set("end", end);
      const resp = await fetch(`${API_BASE}/reports/${kind}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `rentopc-${kind}-${stamp}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(String(e.message || e));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid={V1.reportsPage}>
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
          <FileText size={20} /> Reports
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Export module data as CSV, XLSX or PDF.
        </p>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-4 max-w-2xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Report</span>
            <select value={kind} onChange={(e) => setKind(e.target.value)}
              data-testid={V1.reportsType}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
              {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Format</span>
            <select value={format} onChange={(e) => setFormat(e.target.value)}
              data-testid={V1.reportsFormat}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="xlsx">Excel (.xlsx)</option>
              <option value="csv">CSV</option>
              <option value="pdf">PDF</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">From</span>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
              data-testid={V1.reportsStart}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">To</span>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              data-testid={V1.reportsEnd}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
        </div>
        {err && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            {err}
          </div>
        )}
        <div className="flex justify-end">
          <button onClick={download} disabled={busy}
            data-testid={V1.reportsDownload}
            className="inline-flex items-center gap-2 rounded-md bg-slate-900 text-white px-4 py-2 text-sm hover:bg-slate-800 disabled:opacity-50">
            <Download size={14} /> {busy ? "Preparing…" : "Download"}
          </button>
        </div>
      </div>
    </div>
  );
}
